import { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  useMediaQuery,
} from "@mui/material";
import { toast } from "react-toastify";
import BlueButton from "../../../components/BlueButton/BlueButton";
import Facebook from "./../../../assets/facebook-img.svg";
import Instgram from "./../../../assets/instgram-img.svg";
import X from "./../../../assets/x-img.svg";
import Youtube from "./../../../assets/youtube-img.svg";
import Whats from "./../../../assets/whats-img.svg";
import Telegram from "./../../../assets/telegarm-img.svg";
import Google from "./../../../assets/google-play.svg";
import AppStore from "./../../../assets/apple-store.svg";
import api from "../../../utils/api";
import Loader from "../../../components/Loader";
import { usePermissions } from "../../../hooks/usePermissions";

const Links = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);

  // Check permissions
  const canView = hasPermission(97); // رؤية روابط تواصل معانا
  const canEdit = hasPermission(96); // تعديل روابط التواصل

  // Combined state for all links
  const [allLinks, setAllLinks] = useState({
    faceBookLink: "",
    instagramLink: "",
    xLink: "",
    youTubeLink: "",
    whatsAppLink: "",
    telegramLink: "",
    appStoreLink: "",
    googlePlayLink: "",
  });

  // Fetch all links on component mount
  useEffect(() => {
    const fetchLinks = async () => {
      // Check view permission
      if (!canView) {
        toast.error("ليس لديك صلاحية لعرض روابط التواصل");
        return;
      }

      setLoading(true);
      try {
        // Fetch all links from the single endpoint
        const response = await api.get("/system/socialLinks");
        if (response.data.status === "success") {
          // Convert any null values from API to empty strings
          const formattedLinks = Object.keys(response.data.data).reduce(
            (acc, key) => {
              acc[key] =
                response.data.data[key] === null ? "" : response.data.data[key];
              return acc;
            },
            {}
          );

          setAllLinks(formattedLinks);
        }
      } catch (error) {
        console.error("Error fetching links:", error);
        toast.error("فشل في تحميل الروابط. يرجى المحاولة مرة أخرى.");
      } finally {
        setLoading(false);
      }
    };

    fetchLinks();
  }, [canView, shouldFetch]);

  // Handle all links input changes
  const handleLinkChange = (field) => (e) => {
    if (!canEdit) {
      toast.error("ليس لديك صلاحية لتعديل روابط التواصل");
      return;
    }

    const value = e.target.value;
    setAllLinks({
      ...allLinks,
      [field]: value,
    });
  };

  // Save all links
  const handleSave = async (e) => {
    e.preventDefault();

    // Check edit permission
    if (!canEdit) {
      toast.error("ليس لديك صلاحية لتعديل روابط التواصل");
      return;
    }

    setSaveLoading(true);
    try {
      // Prepare data with empty strings for empty values (not null or undefined)
      const linksToSave = Object.keys(allLinks).reduce((acc, key) => {
        // Ensure we always send a string, even if empty
        acc[key] = allLinks[key] || "";
        return acc;
      }, {});

      // Update all links with a single request
      await api.patch("/system/socialLinks", linksToSave);
      toast.success("تم تحديث الروابط بنجاح");
      setShouldFetch(!shouldFetch);
    } catch (error) {
      console.error("Error saving links:", error);
      toast.error("فشل في حفظ الروابط. يرجى المحاولة مرة أخرى.");
    } finally {
      setSaveLoading(false);
    }
  };

  // If no view permission, show restricted access
  if (!canView) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
          backgroundColor: "white",
          borderRadius: "8px",
        }}
      >
        <p
          style={{
            fontSize: "18px",
            color: "#666",
            textAlign: "center",
          }}
        >
          ليس لديك صلاحية لعرض روابط التواصل
        </p>
      </div>
    );
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="departments-container">
      <form className="links-container" onSubmit={handleSave}>
        {/* Social Media Links */}
        <div className="control-group">
          <img src={Facebook} alt="Facebook" />
          <FormControl
            sx={{ width: isMobile ? "100%" : "700px" }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="facebook-link"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              رابط الفيس بوك
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              id="facebook-link"
              type="text"
              value={allLinks.faceBookLink}
              onChange={handleLinkChange("faceBookLink")}
              disabled={!canEdit}
            />
          </FormControl>
        </div>

        <div className="control-group">
          <img src={Instgram} alt="Instagram" />
          <FormControl
            sx={{ width: isMobile ? "100%" : "700px" }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="instagram-link"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              رابط الانستجرام
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              id="instagram-link"
              type="text"
              value={allLinks.instagramLink}
              onChange={handleLinkChange("instagramLink")}
              disabled={!canEdit}
            />
          </FormControl>
        </div>

        <div className="control-group">
          <img src={X} alt="X (Twitter)" />
          <FormControl
            sx={{ width: isMobile ? "100%" : "700px" }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="x-link"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              رابط اكس
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              id="x-link"
              type="text"
              value={allLinks.xLink}
              onChange={handleLinkChange("xLink")}
              disabled={!canEdit}
            />
          </FormControl>
        </div>

        <div className="control-group">
          <img src={Youtube} alt="YouTube" />
          <FormControl
            sx={{ width: isMobile ? "100%" : "700px" }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="youtube-link"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              رابط اليوتيوب
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              id="youtube-link"
              type="text"
              value={allLinks.youTubeLink}
              onChange={handleLinkChange("youTubeLink")}
              disabled={!canEdit}
            />
          </FormControl>
        </div>

        <div className="control-group">
          <img src={Whats} alt="WhatsApp" />
          <FormControl
            sx={{ width: isMobile ? "100%" : "700px" }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="whatsapp-link"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              رابط الواتساب
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              id="whatsapp-link"
              type="text"
              value={allLinks.whatsAppLink}
              onChange={handleLinkChange("whatsAppLink")}
              disabled={!canEdit}
            />
          </FormControl>
        </div>

        <div className="control-group">
          <img src={Telegram} alt="Telegram" />
          <FormControl
            sx={{ width: isMobile ? "100%" : "700px" }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="telegram-link"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              رابط تيلجرام
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              id="telegram-link"
              type="text"
              value={allLinks.telegramLink}
              onChange={handleLinkChange("telegramLink")}
              disabled={!canEdit}
            />
          </FormControl>
        </div>

        {/* App Store Links */}
        <div className="control-group">
          <img src={Google} alt="Google Play" />
          <FormControl
            sx={{ width: isMobile ? "100%" : "700px" }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="google-play-link"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              رابط جوجل بلاي
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              id="google-play-link"
              type="text"
              value={allLinks.googlePlayLink}
              onChange={handleLinkChange("googlePlayLink")}
              disabled={!canEdit}
            />
          </FormControl>
        </div>

        <div className="control-group">
          <img src={AppStore} alt="App Store" />
          <FormControl
            sx={{ width: isMobile ? "100%" : "700px" }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="app-store-link"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              رابط اب ستور
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              id="app-store-link"
              type="text"
              value={allLinks.appStoreLink}
              onChange={handleLinkChange("appStoreLink")}
              disabled={!canEdit}
            />
          </FormControl>
        </div>

        <div className="action-continer">
          {canEdit ? (
            <BlueButton
              text={saveLoading ? "جاري الحفظ..." : "حفظ"}
              variant="fill"
              size="medium"
              type="submit"
              disabled={saveLoading}
            />
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
              }}
            >
              <p
                style={{
                  fontSize: "16px",
                  color: "#666",
                  margin: 0,
                }}
              >
                أنت في وضع العرض فقط - ليس لديك صلاحية لتعديل روابط التواصل
              </p>
            </div>
          )}
        </div>

        {!canEdit && (
          <div
            style={{
              fontSize: "12px",
              color: "#666",
              fontStyle: "italic",
              textAlign: "center",
              marginTop: "10px",
            }}
          >
            جميع الحقول في وضع القراءة فقط
          </div>
        )}
      </form>
    </div>
  );
};

export default Links;
